import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BRAND_LOGOS,
  BRAND_NAME,
  type BrandLogoVariant,
  DARK_MODE_QUERY,
  type ThemedImageSource,
  VELTA_LOGO,
  VELTA_URL,
} from "@/lib/brand-assets";
import { cn } from "@/lib/utils";

interface ThemedImageProps {
  source: ThemedImageSource;
  alt: string;
  className?: string;
  onError?: () => void;
}

/**
 * An image with a separate dark-mode file, picked by the browser from the
 * colour scheme. The `<picture>` wrapper is `display: contents`, so sizing
 * and margin classes on the image behave as if it were placed directly.
 */
function ThemedImage({ source, alt, className, onError }: ThemedImageProps) {
  return (
    <picture className="contents">
      <source media={DARK_MODE_QUERY} srcSet={source.dark} />
      <img src={source.light} alt={alt} className={className} onError={onError} />
    </picture>
  );
}

interface BrandLogoProps {
  /**
   * `full` is the master logo with its baseline; `compact` drops the baseline
   * for small sizes; `mark` is the DC mark alone, for the collapsed sidebar.
   */
  variant?: BrandLogoVariant;
  /**
   * `auto` follows the colour scheme (blue on light surfaces, white in dark
   * mode); `white` is always the white logo, for De Ceuster blauw surfaces.
   */
  tone?: "auto" | "white";
  /** Alt text, and the fallback text shown if the logo fails to load. */
  name?: string;
  /** Sizing classes: set the height (e.g. `h-10`), width scales with the aspect ratio. */
  className?: string;
}

/**
 * De Ceuster's logo: blue on light surfaces, the white variant in dark mode
 * (or always, with `tone="white"`). Falls back to the name as text if the file
 * fails to load.
 */
export function BrandLogo({
  variant = "full",
  tone = "auto",
  name = BRAND_NAME,
  className,
}: BrandLogoProps) {
  const [failed, setFailed] = useState(false);
  const white = tone === "white";

  if (failed) {
    return (
      <span
        className={cn(
          "font-heading font-bold",
          white ? "text-brand-foreground" : "text-primary",
          className,
        )}
      >
        {name}
      </span>
    );
  }

  const logo = BRAND_LOGOS[variant];
  return (
    <ThemedImage
      source={white ? { light: logo.dark, dark: logo.dark } : logo}
      alt={name}
      className={cn("w-auto object-contain", className)}
      onError={() => setFailed(true)}
    />
  );
}

/** The small "Built by Velta" credit: the only place Velta's brand appears. */
export function VeltaCredit({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <a
      href={VELTA_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        // On hover the credit grows a little with a slight overshoot (a "back-out"
        // curve); it stays still for people who ask for reduced motion.
        "text-muted focus-visible:ring-ring tap-target inline-flex items-center gap-1.5 rounded-sm text-xs transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-[1.06] focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none motion-reduce:hover:scale-100 pointer-coarse:relative",
        className,
      )}
    >
      {t("shell.builtBy")}
      <span className="sr-only">{t("shell.veltaNewTab")}</span>
      <ThemedImage source={VELTA_LOGO} alt="" className="h-3.5 w-auto" />
    </a>
  );
}
